import { useContext, useRef, useEffect, useState } from 'react'
import { Box, styled, Typography } from '@mui/material'
import { useFeature } from '@growthbook/growthbook-react'
import { ConferenceContext } from 'contexts/conference'
import { ConferenceParticipant } from 'contexts/conference/types'
import { WseUserId } from 'api/types'
import { UserState } from 'api/classroom/ClassroomState'
import { MatButton } from 'components/buttons'
import { BreakoutRoomsState } from 'pages/useBreakouts'
import { useGridLayout } from './useGridLayout'
import { ParticipantTile } from '../'
import { SpeakingIndicator } from 'components/speaker-indicator/SpeakerIndicator'
import { ViewCueCardImage } from 'pages/teacher/classroom/components/ViewCueCardsModal'
import { useBreakoutAudioIsolation } from './useFilterParticipants'

const Wrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2)
}))
const RoomWrapper = styled(Box)<{ roomsCount: number; active: boolean }>(({ theme, roomsCount, active }) => ({
    width: `calc(100% / ${roomsCount})`,
    height: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    willChange: 'border-color',
    transition: 'border-color 0.3s ease-out',
    border: `solid ${theme.spacing(0.5)} ${active ? theme.palette.primary?.main : theme.palette.grey?.[100]}`,
    borderRadius: theme.spacing(2),
    overflow: 'hidden'
}))
const RoomHeader = styled(Box)<{ active: boolean }>(({ theme, active }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: theme.spacing(6),
    padding: theme.spacing(2),
    willChange: 'background-color',
    transition: 'background-color 0.3s ease-out',
    backgroundColor: active ? theme.palette.primary?.main : theme.palette.grey?.[100],
    '> div': {
        display: 'flex',
        alignItems: 'center'
    },
    p: {
        fontWeight: '800',
        willChange: 'color',
        transition: 'color 0.3s ease-out',
        color: active ? theme.palette.background?.default : theme.palette.grey?.[800]
    },
    button: {
        padding: `${theme.spacing(0.25)} ${theme.spacing(0.5)}`,
        minWidth: 'auto',
        border: `solid 1px ${theme.palette.grey?.[300]}`,
        '&:hover': {
            border: `solid 1px ${theme.palette.grey?.[300]}`
        }
    }
}))
const ParticipantsWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    height: `calc(100% - ${theme.spacing(6)})`,
    padding: theme.spacing(2),
    '> div': {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        alignContent: 'center'
    }
}))
const TileWrapper = styled(Box)(() => ({
    width: 'var(--tile-width)',
    height: 'var(--tile-height)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px'
}))
const ButtonMessage = styled(Typography)(({ theme }) => ({
    color: '#161C24CC' + ' !important',
    fontSize: theme.spacing(1.875),
    fontWeight: '400 !important'
}))

const BorZoomLayout: React.FC<{
    breakouts: BreakoutRoomsState
    showCueCardSharingModal?: () => void
    cueCardZoomModal?: (data: ViewCueCardImage) => void
    isCueCard?: boolean
}> = ({ breakouts, showCueCardSharingModal, cueCardZoomModal, isCueCard = false }) => {
    const conference = useContext(ConferenceContext)
    const isSubsessionsEnabled = useFeature('goc-sub-session').value as boolean
    const [joiningRoomId, setJoiningRoomId] = useState<number | null>(null)

    // Audio isolation is owned by useBreakoutAudioIsolation (single source of truth)
    useBreakoutAudioIsolation(breakouts)

    useEffect(() => {
        if (!breakouts.isTransitioning) {
            setJoiningRoomId(null)
        }
    }, [breakouts.isTransitioning])

    if (conference.status !== 'session-joined') {
        return null
    }

    const roomARef = useRef<HTMLDivElement | null>(null)
    const roomBRef = useRef<HTMLDivElement | null>(null)
    const roomCRef = useRef<HTMLDivElement | null>(null)
    const roomDRef = useRef<HTMLDivElement | null>(null)
    const refs = [roomARef, roomBRef, roomCRef, roomDRef]
    const rooms = breakouts.rooms
    rooms.forEach(r => {
        const ref = refs[r.id]
        if (ref) {
            useGridLayout(ref, r.users.length)
        }
    })

    const participantData: Map<WseUserId, ConferenceParticipant> = new Map()
    conference.participants.forEach(p => {
        participantData.set(p.wseUserId, p)
    })

    const getParticipantOrFallback = (userId: WseUserId): ConferenceParticipant | null => {
        // Priority 1: Stable backend data — loaded once on class start, never flickers during transitions
        if (isSubsessionsEnabled && breakouts.isActive) {
            const studentInfo = conference.classroomStateOnJoin?.dcAppData?.students?.find((s: UserState) => s.id === userId)
            if (!studentInfo) return null

            const fallback: ConferenceParticipant = {
                clientUserId: `subsession-${userId}`,
                wseUserId: userId,
                firstName: studentInfo.firstName || 'Student',
                lastName: studentInfo.lastName || '',
                wseProfilePhotoUrl: studentInfo.photoUri,
                cameraStatus: 'off',
                audioStatus: 'off',
                role: 'student',
                micHardMuted: studentInfo.micHardMuted ?? false,
                expelled: studentInfo.expelled ?? false
            }

            // Priority 2: Upgrade to live SDK data if the user is actively in my Zoom room (has video/audio)
            if (participantData.has(userId)) {
                return participantData.get(userId)!
            }

            return fallback
        }

        // Non-subsession: use live participant data only
        return participantData.get(userId) ?? null
    }

    const getActiveRoomsCount = (): number => rooms.reduce((count, room) => room.users.length > 0 ? count + 1 : count, 0)
    const speakers = [...conference.speakers].map(s => conference.participants.find(p => p.clientUserId === s)).map(s => s?.wseUserId)

    return (
        <Wrapper id='participants-grid'>
            {rooms.filter(room => room.users.length > 0).map(r => (
                <RoomWrapper id='bor-room-wrapper' key={`Room-${r.id}`} roomsCount={getActiveRoomsCount()} active={breakouts.teacherRoom === r.id}>
                    <RoomHeader id='bor-room-header' active={breakouts.teacherRoom === r.id}>
                        <div>
                            <p>{`Room ${String.fromCharCode('A'.charCodeAt(0) + r.id)}`}</p>
                            {breakouts.teacherRoom !== r.id && r.users.some(u => speakers.includes(u)) ? (
                                <SpeakingIndicator />
                            ) : null}
                        </div>
                        {breakouts.teacherRoom !== r.id ? (
                            <MatButton
                                id='join'
                                variant='contained'
                                color='primary'
                                onClick={() => {
                                    setJoiningRoomId(r.id)
                                    void breakouts.linkTeacher(r.id)
                                }}
                                disabled={breakouts.isTransitioning}
                                className='button-in-active'
                                size='small'
                                width='65px'
                                height='39px'
                                borderRadius='60px'
                            >
                                <ButtonMessage>
                                    {breakouts.isTransitioning && joiningRoomId === r.id ? 'Joining' : 'Join'}
                                </ButtonMessage>
                            </MatButton>
                            // <StandardButton id='bor-join-button' text='Join' color='secondary' onClick={() => breakouts.linkTeacher(r.id)} />
                        ) : null}
                    </RoomHeader>
                    {conference.status === 'session-joined' ? (
                        <ParticipantsWrapper id='bor-room-participants-wrapper'>
                            <div ref={refs[r.id]}>
                                {r.users.map((u: WseUserId, i: number) => {
                                    const participant = getParticipantOrFallback(u)
                                    return participant ? (
                                        <TileWrapper key={u}>
                                            <ParticipantTile
                                                id='participant-tile'
                                                participant={participant}
                                                breakouts={breakouts}
                                                isCueCards={isCueCard}
                                                isTeacher
                                                forceTileBorders
                                                cueCardZoomModal={cueCardZoomModal}
                                                showCueCardSharingModal={showCueCardSharingModal}
                                            />
                                        </TileWrapper>
                                    ) : null
                                })}
                            </div>
                        </ParticipantsWrapper>
                    ) : null}
                </RoomWrapper>
            ))
            }
        </Wrapper>
    )
}

export { BorZoomLayout }
